/**
 * Ce que l'écran « Utilisateurs du personnel » demande à la base.
 *
 * ## Le filtre va à la base, pas au navigateur
 *
 * L'écran chargeait `users` en entier (`.limit(5000)`) pour filtrer ensuite
 * dans le navigateur. Or PostgREST ne rend jamais plus de lignes que son
 * plafond (« Max rows », 1 000 sur ce projet) : `.limit(5000)` n'y change
 * rien, et le reste manquait sans la moindre erreur. Au 28/09/2026 : 2 435
 * comptes ; triés par rôle puis par nom, un parent sans nom arrivait au-delà
 * du 2 000e rang — introuvable par la recherche, donc impossible à promouvoir.
 *
 * Le rôle et la recherche deviennent des filtres de la requête, et la liste se
 * lit par page, avec son total.
 *
 * ## Les parents, seulement sur recherche
 *
 * Règle reprise telle quelle : « Personnel uniquement » ne montre les parents
 * qu'à partir de deux caractères cherchés. On vient chercher un compte précis
 * pour lui donner un rôle, pas parcourir toutes les familles.
 */

import { nettoyerPourIlike } from '../factures/recherche';
import { filtresDeRecherche } from './recherche';

export type RoleDeCompte = 'admin' | 'coordi' | 'anim' | 'parent';
export type FiltreDeRole = 'staff' | 'all' | RoleDeCompte;

export const ROLES_DU_PERSONNEL: readonly RoleDeCompte[] = ['admin', 'coordi', 'anim'];

/** En « Personnel uniquement », les parents apparaissent à partir de ce nombre de caractères. */
export const SAISIE_MIN_POUR_LES_PARENTS = 2;

export interface CritereDuPersonnel {
  /** Les rôles retenus ; `null` : tous. */
  roles: readonly RoleDeCompte[] | null;
  /** Un `or` PostgREST par mot cherché (`filtresDeRecherche`). */
  filtres: string[];
}

export function critereDuPersonnel(
  filtre: FiltreDeRole,
  saisie: string | null | undefined,
): CritereDuPersonnel {
  const filtres = filtresDeRecherche(saisie);
  if (filtre === 'all') return { roles: null, filtres };
  if (filtre !== 'staff') return { roles: [filtre], filtres };

  // Mesurée après nettoyage : « ,, » ne cherche rien et ne doit pas ouvrir
  // la liste à tous les comptes.
  const assez = nettoyerPourIlike(saisie ?? '').length >= SAISIE_MIN_POUR_LES_PARENTS;
  return { roles: assez ? null : ROLES_DU_PERSONNEL, filtres };
}
